// v49.52 — Client-side error reporter.
// Catches uncaught exceptions + unhandled promise rejections and
// posts them to the client_errors table (migration-038). Strips
// browser-extension noise + de-dupes within a session so a single
// runaway loop doesn't burn the rate limit.
//
// Design notes:
//   • One-shot init from App.tsx via initErrorReporter(). Safe to
//     call repeatedly — guarded by a module-level flag.
//   • Each report runs through fetch-keepalive style (Supabase JS
//     handles this) so it survives page unload mid-report.
//   • All failures are swallowed — the reporter must never throw.
//   • Per-session dedupe keyed on `${name}:${message}:${first stack
//     frame}` so a typo'd useState in a tight render loop doesn't
//     spam the table.

import { supabase } from "./api/supabase-client";
import { APP_VERSION } from "./version";

interface ReportContext {
  /* Free-form extra context the caller wants logged with the error. */
  [key: string]: unknown;
}

let _initialised = false;
const _seen = new Set<string>();
const MAX_DEDUPE_SIZE = 100;

/* Patterns we never want to capture — pure noise from browser
   extensions or benign DOM behaviour. */
const IGNORE_PATTERNS: (RegExp | string)[] = [
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-web-extension:\/\//i,
  /^ResizeObserver loop/i,
  /^A listener indicated an asynchronous response/i,
  /Non-Error promise rejection captured/i,
  /Script error\.?$/i,
];

function shouldIgnore(message: string, stack: string): boolean {
  const haystack = `${message}\n${stack}`;
  return IGNORE_PATTERNS.some(p =>
    typeof p === "string" ? haystack.includes(p) : p.test(haystack));
}

function dedupeKey(name: string, message: string, stack: string): string {
  const firstFrame = (stack || "").split("\n").find(l => l.trim().startsWith("at ")) || "";
  return `${name}|${message}|${firstFrame.trim()}`;
}

async function postReport(args: {
  name:    string;
  message: string;
  stack:   string;
  context?: ReportContext;
}) {
  try {
    const session = await supabase.auth.getSession();
    const user    = session?.data?.session?.user || null;
    const row = {
      user_id:       user?.id || null,
      actor_email:   user?.email || null,
      app_version:   APP_VERSION,
      url:           typeof location !== "undefined" ? location.pathname + location.search : null,
      user_agent:    typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
      error_name:    args.name.slice(0, 100),
      error_message: args.message.slice(0, 1000),
      stack:         (args.stack || "").slice(0, 4000),
      context:       args.context || {},
    };
    /* Direct INSERT — anon + authenticated both have insert grant
       (RLS allows; reads blocked). Use the no-await pattern so we
       don't block the original handler. */
    const { error } = await supabase.from("client_errors").insert(row);
    if (error) console.warn("[errorReporter] insert:", error.message);
  } catch (e) {
    /* Swallow — never throw from the reporter. */
    console.warn("[errorReporter] threw:", (e as Error)?.message);
  }
}

export function reportError(name: string, message: string, stack: string, context?: ReportContext) {
  if (shouldIgnore(message, stack)) return;
  const key = dedupeKey(name, message, stack);
  if (_seen.has(key)) return;
  if (_seen.size > MAX_DEDUPE_SIZE) _seen.clear(); /* prevent unbounded growth */
  _seen.add(key);
  void postReport({ name, message, stack, context });
}

export function initErrorReporter() {
  if (_initialised) return;
  if (typeof window === "undefined") return;
  _initialised = true;

  /* Uncaught exceptions (script errors, throws inside event handlers
     not caught by a React boundary, etc). */
  window.addEventListener("error", (e: ErrorEvent) => {
    const err = e.error;
    const name = err?.name || "Error";
    const message = err?.message || e.message || "Unknown error";
    const stack = err?.stack || `at ${e.filename || "?"}:${e.lineno || "?"}:${e.colno || "?"}`;
    reportError(name, message, stack, { source: "window.error" });
  });

  /* Unhandled promise rejections — the original useEffect bug surfaced
     this way. */
  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    const reason = e.reason;
    if (!reason) return;
    const name = reason?.name || "UnhandledRejection";
    const message = reason?.message || String(reason);
    const stack = reason?.stack || "";
    reportError(name, message, stack, { source: "unhandledrejection" });
  });
}

/* Hook for React error boundaries to defer into the same pipeline. */
export function reportBoundaryError(error: Error, info: { componentStack?: string | null }) {
  reportError(
    error.name || "BoundaryError",
    error.message || "React render error",
    `${error.stack || ""}\n${info.componentStack || ""}`,
    { source: "react-error-boundary" },
  );
}

/* v49.57 — User-submitted feedback (bug reports + feature requests).
   Writes to the same client_errors table so manual reports and
   auto-captured exceptions live side by side, sortable by
   occurred_at. Dedupe is bypassed — every submission is its own
   event. */
export type FeedbackCategory = "bug" | "feature" | "other";

export function reportUserFeedback(args: {
  category:     FeedbackCategory;
  message:      string;
  contextExtras?: Record<string, unknown>;
}) {
  void postReport({
    name:    `user_report.${args.category}`,
    message: args.message.slice(0, 1000),
    /* Synthesise a stack-shaped string from the location + agent so
       the row stays diagnostically useful even though there's no
       runtime exception. */
    stack: [
      `URL: ${typeof location !== "undefined" ? location.pathname + location.search : "unknown"}`,
      `User-Agent: ${typeof navigator !== "undefined" ? navigator.userAgent : "unknown"}`,
    ].join("\n"),
    context: {
      source: "user_report",
      category: args.category,
      ...(args.contextExtras || {}),
    },
  });
}
